import React, { useState, useEffect, useMemo } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Loader2, 
  RefreshCw, Target, BarChart3, PieChart, Filter,
  ChevronRight, Tag, UserCheck, UserX, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/services/api';

const RevenuePanel = () => {
  const [revenueData, setRevenueData] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [cohortAnalysis, setCohortAnalysis] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  const sortedAreaData = useMemo(() => {
    if (!revenueData?.by_area) return [];
    return Object.entries(revenueData.by_area)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
  }, [revenueData?.by_area]);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [revenueRes, cohortsRes, analysisRes] = await Promise.all([
        api.get(`/api/admin/revenue?period=${period}`),
        api.get('/api/admin/cohorts'),
        api.get('/api/admin/cohorts/analysis')
      ]);

      if (revenueRes.data.success) {
        setRevenueData(revenueRes.data.revenue);
      }
      if (cohortsRes.data.success) {
        setCohorts(cohortsRes.data.cohorts);
      }
      if (analysisRes.data.success) {
        setCohortAnalysis(analysisRes.data.analysis);
      }
    } catch (error) {
      toast.error('Failed to load revenue data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignCustomers = async (cohortId) => {
    try {
      const response = await api.post(`/api/admin/cohorts/${cohortId}/assign-customers`);
      if (response.data.success) {
        toast.success(`Assigned ${response.data.customers_assigned} customers to cohort`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to assign customers');
    }
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getCohortColor = (cohortType) => {
    const colors = {
      'revenue': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'frequency': 'bg-blue-100 text-blue-800 border-blue-200',
      'custom': 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return colors[cohortType] || 'bg-gray-100 text-gray-800';
  };

  const getObjectiveIcon = (objective) => {
    const icons = {
      'upsell': TrendingUp,
      'cross_sell': Target,
      'retention': UserCheck,
      'reactivation': RefreshCw,
      'loyalty_reward': DollarSign,
      'new_acquisition': Users,
      'referral': Users
    };
    return icons[objective] || Target;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revenue-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Revenue & Customer Analysis</h2>
          <p className="text-gray-500">Track revenue, analyze cohorts, and segment customers</p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Revenue Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">Total Quoted</p>
                <p className="text-2xl font-bold text-emerald-800">
                  {formatCurrency(revenueData?.total_quoted)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Confirmed</p>
                <p className="text-2xl font-bold text-blue-800">
                  {formatCurrency(revenueData?.total_confirmed)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Completed</p>
                <p className="text-2xl font-bold text-green-800">
                  {formatCurrency(revenueData?.total_completed)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Conversion Rate</p>
                <p className="text-2xl font-bold text-purple-800">
                  {revenueData?.total_quoted > 0 
                    ? Math.round((revenueData.total_completed / revenueData.total_quoted) * 100)
                    : 0}%
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Revenue Breakdown</TabsTrigger>
          <TabsTrigger value="cohorts">Customer Cohorts</TabsTrigger>
          <TabsTrigger value="analysis">Cohort Analysis</TabsTrigger>
        </TabsList>

        {/* Revenue Breakdown Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Service */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#039fff]" />
                  Revenue by Service
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Deliveries</span>
                      <span>{formatCurrency(revenueData?.by_service?.delivery || 0)}</span>
                    </div>
                    <Progress 
                      value={revenueData?.total_quoted > 0 
                        ? ((revenueData.by_service?.delivery || 0) / revenueData.total_quoted) * 100 
                        : 0
                      } 
                      className="h-3"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Mini-Moves</span>
                      <span>{formatCurrency(revenueData?.by_service?.minimove || 0)}</span>
                    </div>
                    <Progress 
                      value={revenueData?.total_quoted > 0 
                        ? ((revenueData.by_service?.minimove || 0) / revenueData.total_quoted) * 100 
                        : 0
                      }
                      className="h-3 [&>div]:bg-purple-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top Areas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-[#fb6402]" />
                  Revenue by Area
                </CardTitle>
              </CardHeader>
              <CardContent>
                {revenueData?.by_area && Object.keys(revenueData.by_area).length > 0 ? (
                  <div className="space-y-3">
                    {sortedAreaData.map(([area, amount], index) => (
                        <div key={area} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-gray-100 text-gray-600' :
                              index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-50 text-gray-500'
                            }`}>
                              {index + 1}
                            </span>
                            <span className="truncate max-w-[200px]">{area}</span>
                          </div>
                          <span className="font-medium">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No area data available</p>
                )}
              </CardContent>
            </Card>

            {/* Top Customers */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-500" />
                  Top Customers by Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                {revenueData?.grouped_data && revenueData.grouped_data.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-4 font-medium text-gray-600">Rank</th>
                          <th className="text-left py-2 px-4 font-medium text-gray-600">Customer</th>
                          <th className="text-right py-2 px-4 font-medium text-gray-600">Total Spent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revenueData.grouped_data.map((customer, index) => (
                          <tr key={customer.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                index === 1 ? 'bg-gray-100 text-gray-600' :
                                index === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-50 text-gray-500'
                              }`}>
                                {index + 1}
                              </span>
                            </td>
                            <td className="py-3 px-4 font-medium">{customer.name}</td>
                            <td className="py-3 px-4 text-right font-bold text-green-600">
                              {formatCurrency(customer.total_spent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No customer revenue data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cohorts Tab */}
        <TabsContent value="cohorts" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cohorts.map((cohort) => (
              <Card key={cohort.cohort_id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{cohort.name}</CardTitle>
                      <Badge className={getCohortColor(cohort.cohort_type)}>
                        {cohort.cohort_type}
                      </Badge>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleAssignCustomers(cohort.cohort_id)}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{cohort.description}</p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-[#001A47]">{cohort.customer_count || 0}</p>
                      <p className="text-xs text-gray-500">Customers</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(cohort.total_revenue || 0)}
                      </p>
                      <p className="text-xs text-gray-500">Revenue</p>
                    </div>
                  </div>

                  {cohort.recommended_objectives && cohort.recommended_objectives.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Recommended Actions:</p>
                      <div className="flex flex-wrap gap-1">
                        {cohort.recommended_objectives.map((obj) => {
                          const Icon = getObjectiveIcon(obj);
                          return (
                            <Badge key={obj} variant="outline" className="text-xs flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {obj.replace('_', ' ')}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Rules Preview */}
                  {cohort.rules && Object.keys(cohort.rules).length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500">
                        Rules: {Object.entries(cohort.rules).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Cohort Analysis Tab */}
        <TabsContent value="analysis" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Cohort Performance Analysis</CardTitle>
              <CardDescription>Compare customer segments by revenue and engagement</CardDescription>
            </CardHeader>
            <CardContent>
              {cohortAnalysis.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Cohort</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Customers</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Total Revenue</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Orders</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Avg Order Value</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Recommended Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohortAnalysis.map((analysis) => (
                        <tr key={analysis.cohort_id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="font-medium">{analysis.name}</span>
                          </td>
                          <td className="py-3 px-4 text-right">{analysis.customer_count}</td>
                          <td className="py-3 px-4 text-right font-bold text-green-600">
                            {formatCurrency(analysis.total_revenue)}
                          </td>
                          <td className="py-3 px-4 text-right">{analysis.total_orders}</td>
                          <td className="py-3 px-4 text-right">
                            {formatCurrency(analysis.avg_order_value)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {analysis.recommended_objectives?.slice(0, 2).map((obj) => (
                                <Badge key={obj} variant="outline" className="text-xs">
                                  {obj.replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600">No analysis data</h3>
                  <p className="text-gray-500">Assign customers to cohorts to see analysis</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Segmentation Guide */}
          <Card className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-800">Customer Segmentation Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-medium text-emerald-700 mb-2 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Revenue-Based
                  </h4>
                  <p className="text-sm text-gray-600">
                    High (R2000+), Medium (R500-2000), Low (&lt;R500). Focus upselling on medium, loyalty rewards on high.
                  </p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Frequency-Based
                  </h4>
                  <p className="text-sm text-gray-600">
                    Frequent (5+ orders), First-time, Dormant (60+ days inactive). Reactivate dormant with special offers.
                  </p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-medium text-purple-700 mb-2 flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Type-Based
                  </h4>
                  <p className="text-sm text-gray-600">
                    Business, Student, Individual. Tailor communications and offers to each segment's needs.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RevenuePanel;
